import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getStytchOrgSettings,
  updateStytchOrgSettings,
  createStytchOrganization,
  getStytchOrgStatus,
  getStytchOrgMembers,
  inviteStytchOrgMember,
  getStytchOrgSettingsSelector,
  getIsLoadingStytchOrgSelector,
  getStytchOrgErrorSelector,
  getIsUpdatingStytchOrgSelector,
  getStytchOrgStatusSelector,
  getStytchOrgMembersSelector,
} from "../store/brokerage";
import { getBrokerInformation } from "../store/broker";
import translations from "../utils/translations";

// Enhanced styles (reusing from SSO component)
const styles = {
  container: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    maxWidth: "900px",
    margin: "2rem auto",
    padding: "0 1rem",
    lineHeight: "1.6",
  },
  
  card: {
    border: "1px solid #e1e5e9",
    borderRadius: "12px",
    padding: "2rem",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    marginBottom: "1.5rem",
  },
  
  title: {
    fontSize: "1.5rem",
    fontWeight: "600",
    marginTop: "0",
    marginBottom: "1rem",
    color: "#1f2937",
  },
  
  description: {
    color: "#6b7280",
    marginBottom: "1.5rem",
    fontSize: "1rem",
  },
  
  section: {
    marginBottom: "2rem",
    paddingBottom: "1.5rem",
    borderBottom: "1px solid #e5e7eb",
  },
  
  sectionLast: {
    borderBottom: "none",
    marginBottom: "0",
    paddingBottom: "0",
  },
  
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.75rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
  },
  
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  
  label: {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.5rem",
  },
  
  input: {
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  },
  
  select: {
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    backgroundColor: "white",
  },
  
  textarea: {
    padding: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "0.875rem",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "100px",
  },
  
  helpText: {
    fontSize: "0.75rem",
    color: "#6b7280",
    marginTop: "0.25rem",
  },
  
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  
  checkbox: {
    width: "16px",
    height: "16px",
  },
  
  checkboxLabel: {
    fontSize: "0.875rem",
    color: "#374151",
    cursor: "pointer",
  },
  
  domainsList: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    padding: "1rem",
    marginTop: "0.5rem",
  },
  
  domainItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.5rem",
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    marginBottom: "0.5rem",
  },
  
  domainItemLast: {
    marginBottom: "0",
  },
  
  removeButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.75rem",
    color: "#dc2626",
    backgroundColor: "transparent",
    border: "1px solid #dc2626",
    borderRadius: "4px",
    cursor: "pointer",
  },
  
  addDomainGroup: {
    display: "flex",
    gap: "0.5rem",
    marginTop: "0.5rem",
  },
  
  addButton: {
    padding: "0.5rem 1rem",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#3b82f6",
    backgroundColor: "transparent",
    border: "1px solid #3b82f6",
    borderRadius: "4px",
    cursor: "pointer",
  },
  
  primaryButton: {
    padding: "0.75rem 1.5rem",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  
  primaryButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },
  
  errorText: {
    color: "#dc2626",
    fontSize: "0.875rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "0.75rem",
    marginTop: "1rem",
  },
  
  successText: {
    color: "#16a34a",
    fontSize: "0.875rem",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "6px",
    padding: "0.75rem",
    marginTop: "1rem",
  },
  
  loadingText: {
    color: "#6b7280",
    fontStyle: "italic",
    textAlign: "center",
    padding: "2rem",
  },
  
  infoBox: {
    backgroundColor: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "6px",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  
  infoTitle: {
    color: "#0369a1",
    fontWeight: "600",
    fontSize: "0.875rem",
    marginBottom: "0.5rem",
  },
  
  infoText: {
    color: "#075985",
    fontSize: "0.875rem",
  },
};

function StytchOrgManagement() {
  const dispatch = useDispatch();

  // Language preference setup
  const broker = useSelector(getBrokerInformation);
  const languagepreference = broker.information.languagepreference;
  const stytchOrgTranslations = translations[languagepreference].stytchOrg;

  const orgSettings = useSelector(getStytchOrgSettingsSelector);
  const isLoading = useSelector(getIsLoadingStytchOrgSelector);
  const error = useSelector(getStytchOrgErrorSelector);
  const isUpdating = useSelector(getIsUpdatingStytchOrgSelector);
  const orgStatus = useSelector(getStytchOrgStatusSelector);
  const members = useSelector(getStytchOrgMembersSelector);

  // Debug logging
  console.log('🔎 StytchOrgManagement selector values:', {
    orgSettings,
    isLoading,
    error,
    isUpdating,
    orgStatus,
    members
  });

  const [form, setForm] = useState({
    organization_name: "",
    organization_slug: "",
    organization_logo_url: "",
    email_allowed_domains: [],
    email_invites: "ALL_ALLOWED",
    email_jit_provisioning: "RESTRICTED",
    sso_jit_provisioning: "ALL_ALLOWED",
    auth_methods: "ALL_ALLOWED",
    mfa_policy: "OPTIONAL",
    mfa_methods: "ALL_ALLOWED",
  });

  const [newDomain, setNewDomain] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  useEffect(() => {
    console.log('🔄 StytchOrgManagement: Checking organization status...');
    dispatch(getStytchOrgStatus());
  }, [dispatch]);

  useEffect(() => {
    console.log('🔍 StytchOrgManagement: orgStatus changed:', orgStatus);
    if (orgStatus?.hasOrganization) {
      console.log('✅ Organization exists, loading settings and members...');
      dispatch(getStytchOrgSettings());
      dispatch(getStytchOrgMembers()); // Auto-load members
    } else {
      console.log('❌ No organization found');
    }
  }, [dispatch, orgStatus?.hasOrganization]);

  useEffect(() => {
    console.log('📋 StytchOrgManagement: orgSettings changed:', orgSettings);
    if (orgSettings) {
      console.log('✍️ Populating form with organization settings...');
      setForm({
        organization_name: orgSettings.organization_name || "",
        organization_slug: orgSettings.organization_slug || "",
        organization_logo_url: orgSettings.organization_logo_url || "",
        email_allowed_domains: orgSettings.email_allowed_domains || [],
        email_invites: orgSettings.email_invites || "ALL_ALLOWED",
        email_jit_provisioning: (orgSettings.email_jit_provisioning === "ALL_ALLOWED") ? "RESTRICTED" : (orgSettings.email_jit_provisioning || "RESTRICTED"),
        sso_jit_provisioning: orgSettings.sso_jit_provisioning || "ALL_ALLOWED",
        auth_methods: orgSettings.auth_methods || "ALL_ALLOWED",
        mfa_policy: orgSettings.mfa_policy || "OPTIONAL",
        mfa_methods: orgSettings.mfa_methods || "ALL_ALLOWED",
      });
      console.log('✅ Form populated successfully');
    } else {
      console.log('⚠️ No orgSettings available to populate form');
    }
  }, [orgSettings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addDomain = () => {
    if (newDomain && !form.email_allowed_domains.includes(newDomain)) {
      setForm((f) => ({
        ...f,
        email_allowed_domains: [...f.email_allowed_domains, newDomain]
      }));
      setNewDomain("");
    }
  };

  const removeDomain = (domain) => {
    setForm((f) => ({
      ...f,
      email_allowed_domains: f.email_allowed_domains.filter(d => d !== domain)
    }));
  };

  const handleSubmit = async () => {
    try {
      if (orgStatus.hasOrganization) {
        await dispatch(updateStytchOrgSettings(form));
      } else {
        await dispatch(createStytchOrganization(form));
      }
      // Only show success if no error occurred
      if (!error) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving Stytch org settings:', err);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) return;
    
    try {
      await dispatch(inviteStytchOrgMember({ 
        email_address: inviteEmail 
      }));
      // Only clear email and show success if no error occurred
      if (!error) {
        setInviteEmail("");
        setInviteSuccess(true);
        setTimeout(() => setInviteSuccess(false), 3000);
        // Refresh members list
        dispatch(getStytchOrgMembers());
      }
    } catch (err) {
      console.error('Error inviting member:', err);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>{stytchOrgTranslations.loadingText}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={{...styles.title, fontSize: '2rem', textAlign: 'center', marginBottom: '2rem'}}>
        {stytchOrgTranslations.pageTitle}
      </h2>

      <div style={styles.infoBox}>
        <div style={styles.infoTitle}>📋 {stytchOrgTranslations.organizationConfiguration}</div>
        <div style={styles.infoText}>
          {stytchOrgTranslations.configurationDescription}
        </div>
      </div>

      <div style={styles.card}>
        {/* Basic Organization Info */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{stytchOrgTranslations.basicInformation}</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.organizationName} *</label>
              <input
                name="organization_name"
                value={form.organization_name}
                onChange={handleChange}
                placeholder={stytchOrgTranslations.organizationNamePlaceholder}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <div style={styles.helpText}>
                {stytchOrgTranslations.organizationNameHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.organizationSlug} *</label>
              <input
                name="organization_slug"
                value={form.organization_slug}
                onChange={handleChange}
                placeholder={stytchOrgTranslations.organizationSlugPlaceholder}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <div style={styles.helpText}>
                {stytchOrgTranslations.organizationSlugHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.organizationLogoURL}</label>
              <input
                name="organization_logo_url"
                value={form.organization_logo_url}
                onChange={handleChange}
                placeholder={stytchOrgTranslations.organizationLogoURLPlaceholder}
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
              <div style={styles.helpText}>
                {stytchOrgTranslations.organizationLogoURLHelp}
              </div>
            </div>
          </div>
        </div>

        {/* Email Configuration */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{stytchOrgTranslations.emailConfiguration}</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>{stytchOrgTranslations.allowedEmailDomains}</label>
            <div style={styles.domainsList}>
              {form.email_allowed_domains.map((domain, index) => (
                <div 
                  key={domain} 
                  style={index === form.email_allowed_domains.length - 1 ? 
                    {...styles.domainItem, ...styles.domainItemLast} : styles.domainItem}
                >
                  <span>{domain}</span>
                  <button
                    style={styles.removeButton}
                    onClick={() => removeDomain(domain)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {stytchOrgTranslations.removeDomain}
                  </button>
                </div>
              ))}
              {form.email_allowed_domains.length === 0 && (
                <div style={{color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', padding: '1rem'}}>
                  {stytchOrgTranslations.noDomains}
                </div>
              )}
            </div>
            <div style={styles.addDomainGroup}>
              <input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder={stytchOrgTranslations.domainPlaceholder}
                style={{...styles.input, flex: 1}}
                onKeyPress={(e) => e.key === 'Enter' && addDomain()}
              />
              <button
                style={styles.addButton}
                onClick={addDomain}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                {stytchOrgTranslations.addDomain}
              </button>
            </div>
            <div style={styles.helpText}>
              {stytchOrgTranslations.emailDomainsHelp}
            </div>
          </div>

          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.emailInvites}</label>
              <select
                name="email_invites"
                value={form.email_invites}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="ALL_ALLOWED">{stytchOrgTranslations.allAllowed}</option>
                <option value="RESTRICTED">{stytchOrgTranslations.restricted}</option>
                <option value="NOT_ALLOWED">{stytchOrgTranslations.notAllowed}</option>
              </select>
              <div style={styles.helpText}>
                {stytchOrgTranslations.emailInvitesHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.emailJITProvisioning}</label>
              <select
                name="email_jit_provisioning"
                value={form.email_jit_provisioning}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="RESTRICTED">{stytchOrgTranslations.restricted}</option>
                <option value="NOT_ALLOWED">{stytchOrgTranslations.notAllowed}</option>
              </select>
              <div style={styles.helpText}>
                {stytchOrgTranslations.emailJITProvisioningHelp}
              </div>
            </div>
          </div>
        </div>

        {/* Authentication Methods */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{stytchOrgTranslations.authenticationAndSecurity}</h3>
          
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.authenticationMethods}</label>
              <select
                name="auth_methods"
                value={form.auth_methods}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="ALL_ALLOWED">{stytchOrgTranslations.allAllowed}</option>
                <option value="RESTRICTED">{stytchOrgTranslations.restricted}</option>
              </select>
              <div style={styles.helpText}>
                {stytchOrgTranslations.authenticationMethodsHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.mfaPolicy}</label>
              <select
                name="mfa_policy"
                value={form.mfa_policy}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="OPTIONAL">{stytchOrgTranslations.optional}</option>
                <option value="REQUIRED_FOR_ALL">{stytchOrgTranslations.requiredForAll}</option>
              </select>
              <div style={styles.helpText}>
                {stytchOrgTranslations.mfaPolicyHelp}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{stytchOrgTranslations.mfaMethods}</label>
              <select
                name="mfa_methods"
                value={form.mfa_methods}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="ALL_ALLOWED">{stytchOrgTranslations.allAllowed}</option>
                <option value="RESTRICTED">{stytchOrgTranslations.restricted}</option>
              </select>
              <div style={styles.helpText}>
                {stytchOrgTranslations.mfaMethodsHelp}
              </div>
            </div>
          </div>
        </div>

        {/* SSO Configuration */}
        <div style={{...styles.section, ...styles.sectionLast}}>
          <h3 style={styles.sectionTitle}>{stytchOrgTranslations.ssoConfiguration}</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>{stytchOrgTranslations.ssoJITProvisioning}</label>
            <select
              name="sso_jit_provisioning"
              value={form.sso_jit_provisioning}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="ALL_ALLOWED">{stytchOrgTranslations.allAllowed}</option>
              <option value="RESTRICTED">{stytchOrgTranslations.restricted}</option>
              <option value="NOT_ALLOWED">{stytchOrgTranslations.notAllowed}</option>
            </select>
            <div style={styles.helpText}>
              {stytchOrgTranslations.ssoJITProvisioningHelp}
            </div>
          </div>
        </div>

        {error && (
          <div style={styles.errorText}>
            <strong>{stytchOrgTranslations.errorLabel}</strong> {error}
          </div>
        )}

        {saveSuccess && (
          <div style={styles.successText}>
            <strong>{stytchOrgTranslations.successLabel}</strong> {stytchOrgTranslations.successMessage}
          </div>
        )}

        {inviteSuccess && (
          <div style={styles.successText}>
            <strong>{stytchOrgTranslations.successLabel}</strong> {stytchOrgTranslations.inviteSuccessMessage}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isUpdating}
          style={{
            ...styles.primaryButton,
            ...(isUpdating ? styles.primaryButtonDisabled : {}),
            marginTop: '1.5rem'
          }}
          onMouseEnter={!isUpdating ? (e) => e.target.style.backgroundColor = '#2563eb' : null}
          onMouseLeave={!isUpdating ? (e) => e.target.style.backgroundColor = '#3b82f6' : null}
        >
          {isUpdating ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              {stytchOrgTranslations.updatingSettings}
            </>
          ) : (
            <>💾 {stytchOrgTranslations.saveOrganizationSettings}</>
          )}
        </button>
      </div>

      {/* Members Management Section */}
      {orgStatus?.hasOrganization && (
        <div style={styles.card}>
          <h3 style={styles.title}>{stytchOrgTranslations.organizationMembers}</h3>
          <div style={styles.description}>
            {stytchOrgTranslations.membersDescription}
          </div>

          {/* Invite New Member */}
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>{stytchOrgTranslations.inviteNewMember}</h4>
            <div style={styles.addDomainGroup}>
              <input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={stytchOrgTranslations.memberEmailPlaceholder}
                style={{...styles.input, flex: 1}}
                type="email"
                onKeyPress={(e) => e.key === 'Enter' && handleInviteMember()}
              />
              <button
                style={styles.addButton}
                onClick={handleInviteMember}
                disabled={!inviteEmail || isUpdating}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                {isUpdating ? stytchOrgTranslations.sendingInvitation : stytchOrgTranslations.sendInvitation}
              </button>
            </div>
            <div style={styles.helpText}>
              {stytchOrgTranslations.invitationHelp}
            </div>
          </div>

          {/* Members List */}
          <div style={{...styles.section, ...styles.sectionLast}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={styles.sectionTitle}>{stytchOrgTranslations.currentMembers} ({members?.length || 0})</h4>
              <button
                style={styles.addButton}
                onClick={() => dispatch(getStytchOrgMembers())}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#eff6ff'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                🔄 {stytchOrgTranslations.refresh}
              </button>
            </div>
            
            {members && members.length > 0 ? (
              <div style={styles.domainsList}>
                {members.map((member, index) => (
                  <div 
                    key={member.member_id || member.user_id || index}
                    style={index === members.length - 1 ? 
                      {...styles.domainItem, ...styles.domainItemLast} : styles.domainItem}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <strong>{member.email_address}</strong>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          borderRadius: '12px',
                          backgroundColor: member.status === 'active' ? '#dcfce7' : 
                                         member.status === 'pending' ? '#fef3c7' : '#f3f4f6',
                          color: member.status === 'active' ? '#166534' : 
                                member.status === 'pending' ? '#92400e' : '#374151',
                          border: `1px solid ${member.status === 'active' ? '#bbf7d0' : 
                                              member.status === 'pending' ? '#fed7aa' : '#d1d5db'}`
                        }}>
                          {member.status || 'unknown'}
                        </span>
                      </div>
                      {member.name && (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                          {member.name}
                        </div>
                      )}
                      {member.created_at && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                          {stytchOrgTranslations.joined} {new Date(member.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {member.status === 'pending' && (
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#92400e',
                        backgroundColor: '#fef3c7',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #fed7aa'
                      }}>
                        {stytchOrgTranslations.invitationPending}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                color: '#6b7280', 
                fontSize: '0.875rem', 
                textAlign: 'center', 
                padding: '2rem',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px'
              }}>
                {isLoading ? stytchOrgTranslations.loadingMembers : stytchOrgTranslations.noMembers}
              </div>
            )}
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default StytchOrgManagement;